import React, { useState, useMemo } from 'react';
import { Drawer, message, Modal, Alert, Space, Input, Button } from 'antd';
import { debounce } from 'lodash';
import { useAtomValue } from 'jotai';
import { useSelector } from 'react-redux';
import { isAdminRoleSelector, featureSelector, getUserProfile } from '@store/common/selectors';
import * as atoms from '../utils/atom';
import { DimensionCreatePanel } from './DimensionsCreatePanel';
import {
  Dimension,
  OperationType,
  ACTIVATE_OPERATION,
  DEACTIVATE_OPERATION,
  DELETE_OPERATION,
  CHANGE_NAME_OPERATION,
} from '../utils/types';
import DimensionTable from '../utils/helper';
import * as Styled from '../utils/styled';
import Loading from '../../common/Loading';
import * as Const from '../utils/const';
import { useModal } from '../utils/hooks';
import apptracker from '@utils/sensor/apptracker';
import { DELETE_DIMENSION_ON_DIMENSION_MANAGEMENT } from '@src/utils/sensor/track';
import { CONTEXT_MAP } from 'utils/constants';
import HelpRedirect from '@src/components/common/HelpRedirect';
import { DIMENSION_MANAGEMENT_HELP } from '@src/utils/help/links';

export const DimensionMain = (props: atoms.ManagementProps) => {
  const {
    dimensionVisible = false,
    isDimensionMetaLoading = false,
    isDimensionLoading = false,
    isDimensionError = false,
    isDimensionMetaError = false,
    dimensionsData,
  } = props;

  const isAdminUser = useSelector(isAdminRoleSelector);
  const useProfile = useSelector(getUserProfile);

  const maxDimensionReports = useSelector(featureSelector('max_dimension_reports'));
  const maxAdExpDimensionReports = useSelector(featureSelector('max_ad_exp_dimension_reports'));
  const activeEIDimension = dimensionsData?.dimensions.filter((r) => r.ei).length;
  const activeAIDimension = dimensionsData?.dimensions.filter((r) => r.ai).length;
  const maxInfo = {
    activeEIDimension: activeEIDimension,
    activeAIDimension: activeAIDimension,
    maxEIDimension: maxDimensionReports,
    maxAIDimension: maxAdExpDimensionReports,
  };
  const callbacks = useAtomValue(atoms.callbacks);
  const [searchText, setSearchText] = useState('');
  const { modalVisible, modalLoading, modalParams, showModal, closeModal, setModalLoading } =
    useModal();

  const handleSearchTextChange = useMemo(() => {
    const handle = debounce((value: string) => setSearchText(value), 20);
    return handle;
  }, []);

  const dimensions = useMemo(() => {
    const dimensionMatched =
      dimensionsData?.dimensions.filter((it) => {
        if (!searchText) return true;

        const textStr = String(searchText).toLocaleLowerCase();
        return (
          String(it.name).toLocaleLowerCase().includes(textStr) ||
          String(it.tag_key).toLocaleLowerCase().includes(textStr)
        );
      }) || [];
    return dimensionMatched;
  }, [dimensionsData, searchText]);

  const handleEdition = (record: Dimension, editInfo: string, operation: OperationType) => {
    const handleConfirm = async (
      updatedData: Dimension[],
      operation: string,
      dimension: Dimension,
      product: string,
    ) => {
      try {
        setModalLoading(true);
        await callbacks.onEdit?.({
          configurationVersion: dimensionsData?.configurationVersion || 0,
          dimensions: updatedData,
          configId: dimensionsData?.configId || 0,
          operation: {
            dimensionOperation: operation,
            dimension: dimension,
            product: product,
          },
        });
      } catch (error: any) {
        if (!error?.response?.data?.error) {
          message.error(`${operation} operation failed`);
        }
      } finally {
        setModalLoading(false);
        closeModal();
      }
    };

    if (operation === CHANGE_NAME_OPERATION) {
      showModal({
        title: 'Change Dimension Name?',
        content: Const.CHANGE_NAME_TEXT(record.name, editInfo),
        buttonText: 'Change Dimension',
        onConfirm: async () => {
          // check name should not be duplicated
          const findDp = dimensionsData?.dimensions.find((item) => item.name === editInfo);
          if (findDp) {
            message.error(Const.DIMENSION_NAME_DUPLICATION(findDp.name));
            setModalLoading(false);
            closeModal();
            return;
          }

          const updatedData = dimensionsData?.dimensions.map((item) =>
            item.name === record.name ? { ...item, name: editInfo } : item,
          );
          await handleConfirm(updatedData, operation, record, '');
        },
      });
      return;
    }

    // if dimension limit is reached, show error message
    if (operation === ACTIVATE_OPERATION) {
      if (activeAIDimension >= maxAdExpDimensionReports && editInfo === CONTEXT_MAP.AI) {
        message.error(`Ads ${Const.DIMENSION_EXCEED_ERROR} ${maxAdExpDimensionReports}`);
        return;
      }
      if (activeEIDimension >= maxDimensionReports && editInfo === CONTEXT_MAP.EI) {
        message.error(`Video ${Const.DIMENSION_EXCEED_ERROR} ${maxDimensionReports}`);
        return;
      }
    }

    if (operation === DELETE_OPERATION) {
      apptracker(DELETE_DIMENSION_ON_DIMENSION_MANAGEMENT, {
        dimensionOperation: 'delete clicked',
        dimension: record,
      });
      showModal({
        title: 'Delete Dimension?',
        content: Const.REMOVE_TEXT,
        buttonText: 'Delete Dimension',
        onConfirm: async () => {
          const updatedData = dimensionsData?.dimensions.filter((item) => item !== record) || [];
          await handleConfirm(updatedData, operation, record, '');
        },
      });
    } else {
      // Wire value — IFS compares against PRODUCT.VSI ('VSI') in
      // cleanupSynopsisPostDimensionUpdate. Do NOT rebrand to 'Video' here or
      // deactivations stop matching and orphan synopsis chips remain.
      const product = editInfo === CONTEXT_MAP.EI ? 'VSI' : 'Ads';
      if (operation === ACTIVATE_OPERATION) {
        showModal({
          title: 'Add Dimension?',
          content: Const.ADD_TEXT,
          buttonText: 'Add Dimension',
          onConfirm: async () => {
            const updatedData = dimensionsData?.dimensions.map((item) =>
              item === record ? { ...item, [editInfo]: true } : item,
            );
            await handleConfirm(updatedData, `${ACTIVATE_OPERATION}`, record, product);
          },
        });
      } else {
        showModal({
          title: 'Remove Dimension?',
          content: Const.REMOVE_TEXT,
          buttonText: 'Remove Dimension',
          onConfirm: async () => {
            const updatedData = dimensionsData?.dimensions.map((item) =>
              item === record ? { ...item, [editInfo]: false } : item,
            );
            await handleConfirm(updatedData, `${DEACTIVATE_OPERATION}`, record, product);
          },
        });
      }
    }
  };

  const renderContent = () => {
    if (isDimensionError || isDimensionMetaError) {
      return <Alert message="Failed to load dimension data" type="error" showIcon />;
    }

    if (isDimensionLoading || isDimensionMetaLoading) {
      return <Loading />;
    }

    if (!dimensionsData || !dimensionsData.dimensions) {
      return <Alert message="No dimension data available" type="info" />;
    }

    return (
      <div style={{ padding: '16px' }}>
        {isAdminUser && <DimensionCreatePanel maxInfo={maxInfo} />}
        <Space direction="vertical" style={{ width: '100%' }}>
          <Input.Search
            autoFocus
            value={searchText}
            placeholder="Search"
            allowClear
            style={{ width: 300 }}
            onChange={(e) => handleSearchTextChange(e.target.value)}
            onSearch={handleSearchTextChange}
          />

          <DimensionTable
            data={dimensions}
            handleEdition={handleEdition}
            isAdminUser={isAdminUser}
            maxInfo={maxInfo}
            timeZone={useProfile.timezone}
          />
        </Space>
      </div>
    );
  };

  return (
    <div>
      <Modal
        open={modalVisible}
        title={modalParams.title}
        maskClosable={false}
        closable={false}
        cancelButtonProps={{ disabled: modalLoading }}
        footer={
          <Space>
            <Button onClick={closeModal} disabled={modalLoading}>
              Cancel
            </Button>
            <Button
              style={
                ['Add Dimension', 'Change Dimension'].includes(modalParams.buttonText)
                  ? { fontWeight: 'bold' }
                  : { color: 'red', borderColor: 'red', fontWeight: 'bold' }
              }
              onClick={() => modalParams.onConfirm && modalParams.onConfirm()}
              disabled={modalLoading}
              loading={modalLoading}
            >
              {modalParams.buttonText}
            </Button>
          </Space>
        }
      >
        <p>{modalParams.content}</p>
      </Modal>
      <Drawer
        title="Dimension Management"
        open={dimensionVisible}
        closable={false}
        closeIcon={null}
        styles={{
          wrapper: {
            width: '85%',
            minWidth: '800px',
          },
          body: {
            backgroundColor: '#f6f6f6',
            padding: 0,
            display: 'flex',
            flexDirection: 'column',
            height: '100%',
          },
        }}
        maskClosable={true} // Enable closing on mask click
        onClose={callbacks.onClose}
      >
        <Styled.HelpRedirectWrapper>
          <HelpRedirect redirectUrl={DIMENSION_MANAGEMENT_HELP} />
        </Styled.HelpRedirectWrapper>
        <Styled.CloseIcon onClick={callbacks?.onClose} />
        {renderContent()}
      </Drawer>
    </div>
  );
};
